import { Effect as BABYLON_Effect } from '@inter-ikea-betrakta-babylonjs/core/Materials/effect.js';
import { PBRMaterial as BABYLON_PBRMaterial } from '@inter-ikea-betrakta-babylonjs/core/Materials/PBR/pbrMaterial.js';
import { SerializationHelper as BABYLON_SerializationHelper } from '@inter-ikea-betrakta-babylonjs/core/Misc/decorators.js';
import { Tools as BABYLON_Tools } from '@inter-ikea-betrakta-babylonjs/core/Misc/tools.js';
import { BabylonLineLight } from '../BabylonLineLight.js';
import '../shaders/ShadersInclude/lightUboDeclaration.js';
import '../shaders/ShadersInclude/lightVxUboDeclaration.js';
import '../shaders/ShadersInclude/lightFragmentDeclaration.js';
import '../shaders/ShadersInclude/lightFragment.js';
import '../shaders/ShadersInclude/pbrDirectLightingSetupFunctions.js';
export const ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME = 'viewer_ar_atttribute';
const ALBEDO_ROUGHNESS_UNIFORM_NAME = 'viewer_ar';
const ALBEDO_ROUGHNESS_DEFINE = 'VIEWER_ALBEDO_ROUGHNESS';
const SHADER_NAME = 'viewer_pbr';
export class ViewerMaterial extends BABYLON_PBRMaterial {
    static create_shaders(ubo_support) {
        // We manipulate the standard shaders in a way such that they are the same unless new defines are set
        // Hence we don't change the shader name to a custom shader name
        let fs = BABYLON_Effect.ShadersStore['pbrPixelShader'];
        // We need to resolve some includes in order to be able to see/change their custom #define entrypoints
        fs = fs.replace(/#include<pbrBlockAlbedoOpacity>/g, BABYLON_Effect.IncludesShadersStore['pbrBlockAlbedoOpacity']);
        fs = fs.replace(/#include<pbrBlockReflectivity>/g, BABYLON_Effect.IncludesShadersStore['pbrBlockReflectivity']);
        let vs = BABYLON_Effect.ShadersStore['pbrVertexShader'];
        // NOTE: Webgl1 shaders!
        vs = vs.replace('#define CUSTOM_VERTEX_DEFINITIONS', `#ifdef ${ALBEDO_ROUGHNESS_DEFINE}
  #ifdef INSTANCES
    attribute vec4 ${ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME};
    varying vec4 ${ALBEDO_ROUGHNESS_UNIFORM_NAME};
  #endif
#endif`);
        vs = vs.replace('#define CUSTOM_VERTEX_MAIN_BEGIN', `#ifdef ${ALBEDO_ROUGHNESS_DEFINE}
  #ifdef INSTANCES
    ${ALBEDO_ROUGHNESS_UNIFORM_NAME} = ${ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME};
  #endif
#endif`);
        fs = fs.replace('#define CUSTOM_FRAGMENT_DEFINITIONS', `#ifdef ${ALBEDO_ROUGHNESS_DEFINE}
  #ifdef INSTANCES
    varying vec4 ${ALBEDO_ROUGHNESS_UNIFORM_NAME};
  #else
    uniform vec4 ${ALBEDO_ROUGHNESS_UNIFORM_NAME};
  #endif
#endif`);
        fs = fs.replace('#define CUSTOM_FRAGMENT_UPDATE_ALBEDO', `#ifdef ${ALBEDO_ROUGHNESS_DEFINE}
   surfaceAlbedo *= ${ALBEDO_ROUGHNESS_UNIFORM_NAME}.xyz;
#endif`);
        fs = fs.replace('#define CUSTOM_FRAGMENT_UPDATE_METALLICROUGHNESS', `#ifdef ${ALBEDO_ROUGHNESS_DEFINE}
   metallicRoughness.g *= ${ALBEDO_ROUGHNESS_UNIFORM_NAME}.w;
#endif`);
        BABYLON_Effect.RegisterShader(SHADER_NAME, fs, vs);
        let linelight_vs = BABYLON_Effect.ShadersStore['pbrVertexShader'];
        const linelight_fs = BABYLON_Effect.ShadersStore['pbrPixelShader'];
        linelight_vs = linelight_vs.replace(/#include<lightsFragmentFunctions>/g, BABYLON_Effect.IncludesShadersStore['lightsFragmentFunctions']);
        BABYLON_Effect.RegisterShader('pbrPixelShader', linelight_fs, linelight_vs);
    }
    constructor(name, scene, viewer) {
        super(name, scene);
        this._enable_albedo_roughness_factor = false;
        this._clip_plane_bboxes = new Array(6);
        this._viewer = viewer;
        this.customShaderNameResolve = this.override_effect;
        // These are true by default - which utterly breaks alpha blending on reflections
        this.useRadianceOverAlpha = false;
        this.useSpecularOverAlpha = false;
        this.useAlphaFromAlbedoTexture = true;
    }
    override_effect(shader_name, uniforms, uniform_buffers, samplers, defines, attributes) {
        const d = defines;
        const current_albedo = defines[ALBEDO_ROUGHNESS_DEFINE];
        let light_exists = true;
        let light_index = 0;
        // Test all lights if they are line lights
        // TODO Ask Babylon for list of active lights in scene and their respective lightindex and iterate over that instead
        while (light_exists) {
            light_exists = defines['LIGHT' + light_index];
            if (light_exists) {
                // Check if it's a linelight type of light
                if (defines['LINELIGHT' + light_index]) {
                    // if so push all the uniforms needed for webgl1
                    uniforms.push(BabylonLineLight.LINE_A_UNIFORM + light_index);
                    uniforms.push(BabylonLineLight.LINE_B_UNIFORM + light_index);
                    uniforms.push(BabylonLineLight.LINE_NORMAL_AND_ANGLE_RANGE_UNIFORM + light_index);
                    uniforms.push(BabylonLineLight.LINE_CLIP_PLANE_NORMAL_DISTANCE + light_index);
                }
            }
            light_index++;
        }
        const use_instances = defines['INSTANCES'];
        if (current_albedo === undefined && !this._enable_albedo_roughness_factor) {
            // No need to change anything
            return shader_name;
        }
        if (this._enable_albedo_roughness_factor && attributes) {
            if (use_instances) {
                attributes.push(ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME);
            }
            else {
                uniforms.push(ALBEDO_ROUGHNESS_UNIFORM_NAME);
            }
        }
        // If same as last time we are happy
        if (current_albedo === this._enable_albedo_roughness_factor) {
            return this._enable_albedo_roughness_factor ? SHADER_NAME : shader_name;
        }
        if (current_albedo === undefined) {
            if (this._enable_albedo_roughness_factor) {
                // @ts-ignore
                d._keys.push(ALBEDO_ROUGHNESS_DEFINE); // I wish there was a better way to accomplish this without rebuilding it fully
                defines[ALBEDO_ROUGHNESS_DEFINE] = true;
                if (use_instances) {
                    d.markAsAttributesDirty();
                }
                else {
                    d.markAllAsDirty(); // TODO-NOTE: We want to mark uniforms as dirty but not sure how
                }
            }
        }
        else if (current_albedo !== this._enable_albedo_roughness_factor) {
            defines[ALBEDO_ROUGHNESS_DEFINE] = this._enable_albedo_roughness_factor; // When we turn it off we leave it in MaterialDefines so we are quicker to enable it next time
            if (use_instances) {
                d.markAsAttributesDirty();
            }
            else {
                d.markAllAsDirty(); // TODO-NOTE: We want to mark uniforms as dirty but not sure how
            }
        }
        return SHADER_NAME;
    }
    set enable_albedo_roughness_factor(value) {
        if (value !== this._enable_albedo_roughness_factor) {
            this._enable_albedo_roughness_factor = value;
            this.markDirty();
        }
    }
    get enable_albedo_roughness_factor() {
        return this._enable_albedo_roughness_factor;
    }
    clone(name) {
        const clone = BABYLON_SerializationHelper.Clone(() => new ViewerMaterial(name, this.getScene(), this._viewer), this);
        clone.id = BABYLON_Tools.RandomId();
        clone.name = name + ' clone';
        // NOTE: This is not forward-proof but we can't change the type being created in super.clone
        //       Would be better if we could create the new material and then do super.copy
        this.clearCoat.copyTo(clone.clearCoat);
        this.anisotropy.copyTo(clone.anisotropy);
        this.brdf.copyTo(clone.brdf);
        this.sheen.copyTo(clone.sheen);
        this.subSurface.copyTo(clone.subSurface);
        return clone;
    }
    dispose(force_dispose_effect, force_dispose_textures) {
        super.dispose(true, true);
        this._bumpTexture = null;
        this._albedoTexture = null;
        this._ambientTexture = null;
        this._opacityTexture = null;
        this._emissiveTexture = null;
        this._lightmapTexture = null;
        this._metallicTexture = null;
        this._reflectionTexture = null;
        this._reflectanceTexture = null;
        this._microSurfaceTexture = null;
        this._environmentBRDFTexture = null;
        this._metallicReflectanceTexture = null;
        // @ts-ignore
        this._eventInfo = {}; // This is needed or the ArrayBuffers will be prevented from being GCed.
    }
}
ViewerMaterial.active_line_lights = new Array();
// BABYLON__TypeStore.RegisteredTypes['PBRMaterial'] = PBRMaterial
//# sourceMappingURL=ViewerMaterial.js.map